// libraries
import type { FldLenderDecision } from '@makemydeal/dr-dash-types';
import { DealerDecisionStatus } from '@makemydeal/dr-dash-types';
import type { Lender, LenderAddress } from '@makemydeal/dr-platform-types';

const KNOWN_STATUSES = [DealerDecisionStatus.APPROVED, DealerDecisionStatus.CONDITIONALLY_APPROVED, DealerDecisionStatus.DECLINED];

export type GroupedLendersByDecisionStatus = {
    approved: (Lender & LenderAddress)[];
    counterOffer: (Lender & LenderAddress)[];
    declined: (Lender & LenderAddress)[];
    other: (Lender & LenderAddress)[];
};

export function groupLendersByDecisionStatus(
    lenderList: (Lender & LenderAddress)[],
    lenderDecisions: FldLenderDecision[]
): GroupedLendersByDecisionStatus {
    const decisionMap = new Map<string, FldLenderDecision>();
    for (const decision of lenderDecisions) {
        if (decision.lenderId) decisionMap.set(decision.lenderId, decision);
    }

    const groupedLenders: GroupedLendersByDecisionStatus = {
        approved: [],
        counterOffer: [],
        declined: [],
        other: []
    };

    for (const lender of lenderList) {
        const decision = decisionMap.get(lender.lenderId);

        switch (decision?.status) {
            case DealerDecisionStatus.APPROVED:
                groupedLenders.approved.push(lender);
                break;
            case DealerDecisionStatus.CONDITIONALLY_APPROVED:
                groupedLenders.counterOffer.push(lender);
                break;
            case DealerDecisionStatus.DECLINED:
                groupedLenders.declined.push(lender);
                break;
            default:
                groupedLenders.other.push(lender);
        }
    }

    return groupedLenders;
}

/**
 * Orders lenders from the lenderList by their official lender provided decision status.
 * Order: Approved → Counter Offer → Declined → Other Lenders (fallback).
 */
export function orderLendersByDecisionStatus(
    lenderList: (Lender & LenderAddress)[],
    lenderDecisions: FldLenderDecision[]
): (Lender & LenderAddress)[] {
    const grouped = groupLendersByDecisionStatus(lenderList, lenderDecisions);
    return [...grouped.approved, ...grouped.counterOffer, ...grouped.declined, ...grouped.other];
}

/**
 * Returns true if the status is a known FLD decision status (Approved, ConditionallyApproved, Declined).
 * Unreturned or unrecognized statuses (e.g., Pending, null, empty) return false.
 */
export function isDecisionStatus(status?: string | null): status is string {
    return !!status && (KNOWN_STATUSES as string[]).includes(status);
}

/**
 * Finds a lender decision by lenderId from an array of FldLenderDecision.
 */
export function findLenderDecisionById(decisions: FldLenderDecision[], lenderId: string): FldLenderDecision | undefined {
    return decisions.find((d) => d.lenderId === lenderId);
}

export function getLenderDecision(
    creditDecisionStatus: string | undefined,
    lender: { id: string } | undefined,
    allLenderDecisions: FldLenderDecision[]
): string {
    const manualStatus = creditDecisionStatus || '';

    if (!lender) return manualStatus;

    const lenderDecision = findLenderDecisionById(allLenderDecisions, lender.id);

    // If lender has provided a decision, use it; otherwise use manually set creditDecisionStatus
    if (lenderDecision && isDecisionStatus(lenderDecision.status)) {
        return lenderDecision.status;
    }

    return manualStatus;
}
